import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ExchangeActionEnum } from "@b2bportal/air-exchange-api";
import {
  type DeparturePageFetchProps,
  FlightExchangeSearchSelectors,
  FlightExchangeThunk,
  FlightPostBookingSelectors,
  FlightSearchSelectors,
  FlightShopSelectors,
  initializeSearchState,
} from "@b2bportal/core-flights";
import type { AppDispatch } from "@b2bportal/core-types";
import { useIsSameSearch } from "../../../../search/hooks";

export const useExchangeDeparturePageFetch = (
  props: DeparturePageFetchProps
) => {
  const dispatch = useDispatch<AppDispatch>();
  const isSameSearch = useIsSameSearch();
  const isInitializing =
    useSelector(FlightSearchSelectors.isInitializingValues) ?? false;
  const { isLoading } = useSelector(FlightShopSelectors.getFlights);
  const exchangeForType = useSelector(
    FlightExchangeSearchSelectors.getExchangeForType
  );
  const originalReservationId = useSelector(
    FlightPostBookingSelectors.getSelectedFlightItinerary
  ).data?.bookedItinerary.id;

  useEffect(() => {
    if (
      !isSameSearch &&
      !isLoading &&
      !isInitializing &&
      originalReservationId != null
    ) {
      dispatch(initializeSearchState(props)).then(() =>
        // Testing note: Exchange shop will not workin in dev/staging, so the exchange flow will
        // always break at this step. To test the shopping/checkout flows, you can replace the call
        // to fetchExchangeFlights with fetchFlights, which will allow you to proceed to the next steps.
        // dispatch(FlightShopThunk.fetchFlights())
        dispatch(
          FlightExchangeThunk.fetchExchangeFlights({
            originalReservationId,
            exchangeType: {
              AirExchangeForType: exchangeForType.exchangeFor,
              outboundSelection: {
                ExchangeAction: exchangeForType.outboundSelection,
              },
              // Only include returnSelection for round-trip exchanges
              // For one-way trips, returnSelection should be undefined/not sent to API
              ...(exchangeForType.returnSelection != null
                ? {
                    returnSelection: {
                      ExchangeAction: exchangeForType.returnSelection,
                    },
                  }
                : {}),
            } as any, // Type cast needed - API type requires returnSelection but rejects it for one-way trips
          })
        )
      );
    }
  }, [
    dispatch,
    props,
    isInitializing,
    isLoading,
    isSameSearch,
    originalReservationId,
    exchangeForType,
  ]);
};
